/**
 * Trades you are part of.
 *
 * No prices anywhere, and nowhere to put one. tomo never displays what
 * something is "worth" or suggests a trade is fair — that is the surface that
 * makes scamming profitable, and it is a product constraint with schema
 * consequences rather than a thing this screen chose not to render.
 */
import { useState, type ReactNode } from 'react';
import type { Trade } from '@tomo/contracts';
import { Shell } from '../components/Shell.jsx';
import { Badge, Button, Empty, Failed, Link, Spinner } from '../components/primitives.jsx';
import { Field, Text } from '../components/form.jsx';
import { ApiError } from '../lib/api.js';
import { trades } from '../lib/tomo.js';
import { useAsync } from '../useAsync.js';
import { useSession } from '../Session.jsx';
import { hrefFor, navigate } from '../lib/router.js';

export function Trades(): ReactNode {
  const session = useSession();
  const [result, retry] = useAsync((signal) => trades.mine(signal), []);
  const [withUid, setWithUid] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = (): void => {
    setBusy(true);
    setError(null);
    trades.open([session.user.uid, withUid.trim()]).then(
      (trade) => { navigate(hrefFor(['trades', trade.id])); },
      (err: unknown) => {
        setBusy(false);
        setError(err instanceof ApiError ? err.message : 'Could not open that trade.');
      }
    );
  };

  return (
    <Shell>
      <h1 className="text-xl font-semibold tracking-tight">Trades</h1>
      <p className="mt-1 text-sm text-ink-400">
        Items move between people. Currency never does — it stays on the channel that issued it.
      </p>

      <form
        className="mt-8 flex max-w-lg items-end gap-3"
        onSubmit={(e) => { e.preventDefault(); open(); }}
      >
        <span className="flex-1">
          <Field label="Trade with" hint="Their tomo account id.">
            <Text value={withUid} onChange={setWithUid} placeholder="u_…" />
          </Field>
        </span>
        <Button type="submit" disabled={busy || withUid.trim().length === 0}>
          {busy ? 'Opening…' : 'Open a trade'}
        </Button>
      </form>

      {error ? <div className="mt-4 max-w-lg"><Failed message={error} /></div> : null}

      <div className="mt-10">
        {result.state === 'loading' ? <Spinner label="Loading your trades…" /> : null}
        {result.state === 'failed' ? (
          <Failed message="Could not load your trades." detail={result.error.message} onRetry={retry} />
        ) : null}
        {result.state === 'ready' && result.value.length === 0 ? (
          <Empty title="No trades yet." hint="Open one above to start a negotiation." />
        ) : null}
        {result.state === 'ready' && result.value.length > 0 ? (
          <ul className="divide-y divide-ink-800 rounded-lg border border-ink-800">
            {result.value.map((trade) => (
              <li key={trade.id}>
                <Link
                  href={hrefFor(['trades', trade.id])}
                  className="flex items-center gap-4 px-4 py-3 hover:bg-ink-900"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-ink-50">
                      with {trade.participants.filter((p) => p !== session.user.uid).join(', ')}
                    </span>
                    <span className="block truncate font-mono text-xs text-ink-700">{trade.id}</span>
                  </span>
                  <Status status={trade.status} />
                </Link>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </Shell>
  );
}

export function Status({ status }: { status: Trade['status'] }): ReactNode {
  switch (status) {
    case 'settled': return <Badge tone="good">Settled</Badge>;
    case 'cancelled': return <Badge>Cancelled</Badge>;
    case 'pending_settle': return <Badge tone="brand">Settling</Badge>;
    case 'draft': return <Badge tone="warn">Negotiating</Badge>;
  }
}
